import { useCallback, useState } from 'react'
import { useAuth } from '@clerk/tanstack-react-start'
import { useQuery } from '@tanstack/react-query'
import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from 'convex/_generated/api'
import type { Id } from 'convex/_generated/dataModel'

interface GuideProgress {
  projectId: Id<'projects'> | null
  completedSteps: number
  totalSteps: number
  progressPercent: number
  isStepCompleted: (stepIndex: number) => boolean
  handleStepComplete: (stepIndex: number, completed: boolean) => Promise<void>
  isStepUpdating: (stepIndex: number) => boolean
}

export function useGuideProgress(templateSlug: string): GuideProgress {
  const { isSignedIn } = useAuth()
  const [updatingIndexes, setUpdatingIndexes] = useState<Set<number>>(() => new Set())

  const { data: progress } = useQuery(
    convexQuery(
      api.diy.projects.getProjectForTemplateSlug,
      isSignedIn ? { templateSlug } : 'skip'
    )
  )

  const updateStepProgress = useConvexMutation(api.diy.projects.updateStepProgress)

  const handleStepComplete = useCallback(
    async (stepIndex: number, completed: boolean) => {
      if (!progress?._id) return
      setUpdatingIndexes((prev) => new Set(prev).add(stepIndex))
      try {
        await updateStepProgress({ projectId: progress._id, stepIndex, completed })
      } finally {
        setUpdatingIndexes((prev) => {
          const next = new Set(prev)
          next.delete(stepIndex)
          return next
        })
      }
    },
    [progress?._id, updateStepProgress]
  )

  const isStepCompleted = useCallback(
    (stepIndex: number) =>
      progress?.stepProgress.find((sp) => sp.stepIndex === stepIndex)?.completed ?? false,
    [progress]
  )

  const isStepUpdating = useCallback(
    (stepIndex: number) => updatingIndexes.has(stepIndex),
    [updatingIndexes]
  )

  if (!isSignedIn || !progress) {
    return {
      projectId: null,
      completedSteps: 0,
      totalSteps: 0,
      progressPercent: 0,
      isStepCompleted: () => false,
      handleStepComplete: async () => {},
      isStepUpdating: () => false,
    }
  }

  return {
    projectId: progress._id,
    completedSteps: progress.completedSteps,
    totalSteps: progress.totalSteps,
    progressPercent: progress.progressPercent,
    isStepCompleted,
    handleStepComplete,
    isStepUpdating,
  }
}
